<template>
  <div>
    <!-- Upgrade Subscription Modal (hidden until online upgrade is enabled) -->
    <CModal
      v-if="upgradeEnabled"
      :show.sync="showUpgradeModal"
      :centered="true"
      size="lg"
    >
      <template #header>
        <h5 class="modal-title">Upgrade your plan</h5>
        <CButtonClose aria-label="Close" @click="showUpgradeModal = false" />
      </template>
      <div>
        <h2 class="h6">Choose a plan</h2>
        <CRow>
          <CCol v-for="item in packageItems" :key="item.id" sm="12" xl="4">
            <CCard>
              <CCardBody class="text-center">
                <div class="font-weight-bold">{{ item.name }}</div>
                <hr class="my-2" />
                <p v-html="formatHtml(item.description)"></p>
                <hr class="my-2" />
                <div class="h5">RM {{ formatMyr(item.price) }}</div>
                <CButton
                  block
                  :color="isSelectedColor(item)"
                  @click="onSelectPackage(item)"
                  >Choose plan</CButton
                >
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        <div class="mt-4" v-if="selectedPackage">
          <CSelect
            label="Payment method"
            :options="paymentMethodOptions"
            :value.sync="selectedPaymentMethod"
          />
        </div>
        <div v-if="selectedPaymentMethod === 'CreditCard'" class="mt-4">
          <div ref="cardElement" id="card-element" class="form-control h-auto py-2"></div>
          <CAlert
            v-if="cardError"
            color="danger"
            id="card-errors"
            role="alert"
            class="mt-2"
          >
            {{ cardError }}
          </CAlert>
        </div>
      </div>
      <template #footer>
        <CButton color="light" :disabled="paying" @click="showUpgradeModal = false">
          Cancel
        </CButton>
        <CButton
          v-if="selectedPaymentMethod === 'CreditCard'"
          color="primary"
          :disabled="paying"
          @click="proceedToPayment"
        >
          <CSpinner v-if="paying" size="sm" class="mr-1" />
          Pay RM {{ formatMyr(selectedPackage && selectedPackage.price) }}
        </CButton>
      </template>
    </CModal>

    <!-- Page header -->
    <CCard class="mb-4">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="8">
            <h1 class="h4 mb-1">Subscription</h1>
            <p class="text-muted mb-0">Your current plan and subscription history</p>
          </CCol>
          <CCol md="4" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton
              color="light"
              aria-label="Refresh"
              :disabled="loading"
              @click="refreshTable"
            >
              <CIcon name="cil-reload" />
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <div v-if="loading && !subscriptionHistory.length" class="text-center p-5">
      <CSpinner color="primary" />
    </div>
    <CCard v-else-if="error && !subscriptionHistory.length">
      <CCardBody class="text-center p-5">
        <CIcon name="cil-warning" size="xl" class="text-danger mb-2" />
        <h2 class="h6">Couldn't load your subscription</h2>
        <p class="text-muted">Check your connection and try again.</p>
        <CButton color="primary" variant="outline" @click="refreshTable">
          <CIcon name="cil-reload" /> Retry
        </CButton>
      </CCardBody>
    </CCard>
    <template v-else>
      <!-- Active subscription -->
      <CCard>
        <CCardHeader>
          <h2 class="h6 mb-0">Current plan</h2>
        </CCardHeader>
        <CCardBody>
          <CRow v-if="activeSubscription">
            <CCol sm="6" lg="3" class="mb-3">
              <div class="small text-muted">Plan</div>
              <div>{{ activeSubscription.packageName }}</div>
            </CCol>
            <CCol sm="6" lg="3" class="mb-3">
              <div class="small text-muted">Period</div>
              <div>
                {{ formatDate(activeSubscription.startDate) }} –
                {{ formatDate(activeSubscription.endDate) }}
              </div>
            </CCol>
            <CCol sm="6" lg="3" class="mb-3">
              <div class="small text-muted">Amount</div>
              <div>RM {{ formatMyr(activeSubscription.amount) }}</div>
            </CCol>
            <CCol sm="6" lg="3" class="mb-3">
              <div class="small text-muted">Status</div>
              <CBadge :color="statusColor(activeSubscription.statusDescription)">
                {{ statusLabel(activeSubscription.statusDescription) }}
              </CBadge>
            </CCol>
            <CCol v-if="upgradeEnabled && activeSubscription.packageName === 'Free'" sm="12">
              <CButton color="primary" @click="upgradeToPro(activeSubscription)">
                Upgrade plan
              </CButton>
            </CCol>
          </CRow>
          <div v-else class="text-center p-4">
            <CIcon name="cil-credit-card" size="xl" class="text-muted mb-2" />
            <h3 class="h6">No active plan</h3>
            <p class="text-muted mb-0">Your current plan will show here once it starts.</p>
          </div>
        </CCardBody>
      </CCard>

      <!-- Subscription history -->
      <CCard>
        <CCardHeader>
          <h2 class="h6 mb-0">History</h2>
        </CCardHeader>
        <CCardBody>
          <div v-if="!computedSubscriptionHistory.length" class="text-center p-4">
            <p class="text-muted mb-0">No subscriptions yet.</p>
          </div>
          <template v-else>
            <div class="d-none d-lg-block">
              <CDataTable
                :items="computedSubscriptionHistory"
                :fields="fields"
                column-filter
                items-per-page-select
                :items-per-page="25"
                hover
                sorter
                pagination
                :loading="loading"
              >
                <template #startDate="{ item }">
                  <td>{{ formatDate(item.startDate) }}</td>
                </template>
                <template #endDate="{ item }">
                  <td>{{ formatDate(item.endDate) }}</td>
                </template>
                <template #isActive="{ item }">
                  <td>
                    <CBadge :color="item.isActive ? 'success' : 'secondary'">
                      {{ item.isActive ? "Active" : "Inactive" }}
                    </CBadge>
                  </td>
                </template>
                <template #statusDescription="{ item }">
                  <td>
                    <CBadge :color="statusColor(item.statusDescription)">
                      {{ statusLabel(item.statusDescription) }}
                    </CBadge>
                  </td>
                </template>
                <template #amount="{ item }">
                  <td class="text-right">RM {{ formatMyr(item.amount) }}</td>
                </template>
                <template #show_details="{ item }">
                  <td class="py-2 text-right text-nowrap">
                    <CButton
                      color="primary"
                      variant="ghost"
                      :aria-label="`Edit ${item.packageName} subscription`"
                      @click="onEdit(item)"
                    >
                      <CIcon name="cil-pencil" />
                    </CButton>
                    <CButton
                      color="danger"
                      variant="ghost"
                      :aria-label="`Delete ${item.packageName} subscription`"
                      @click="showDeleteConfirmation(item)"
                    >
                      <CIcon name="cil-trash" />
                    </CButton>
                  </td>
                </template>
              </CDataTable>
            </div>
            <div class="d-lg-none">
              <CCard v-for="item in computedSubscriptionHistory" :key="item.id" class="mb-2">
                <CCardBody class="p-3">
                  <div class="d-flex justify-content-between align-items-start">
                    <div>
                      <div class="font-weight-bold">{{ item.packageName }}</div>
                      <div class="small text-muted">
                        {{ formatDate(item.startDate) }} – {{ formatDate(item.endDate) }}
                      </div>
                      <div class="font-weight-bold">RM {{ formatMyr(item.amount) }}</div>
                    </div>
                    <CBadge :color="statusColor(item.statusDescription)">
                      {{ statusLabel(item.statusDescription) }}
                    </CBadge>
                  </div>
                  <div class="mt-2 text-right">
                    <CButton
                      color="primary"
                      variant="ghost"
                      :aria-label="`Edit ${item.packageName} subscription`"
                      @click="onEdit(item)"
                    >
                      <CIcon name="cil-pencil" />
                    </CButton>
                    <CButton
                      color="danger"
                      variant="ghost"
                      :aria-label="`Delete ${item.packageName} subscription`"
                      @click="showDeleteConfirmation(item)"
                    >
                      <CIcon name="cil-trash" />
                    </CButton>
                  </div>
                </CCardBody>
              </CCard>
            </div>
          </template>
        </CCardBody>
      </CCard>

      <!-- Invoices and payments -->
      <CCard v-if="invoices && invoices.length">
        <CCardHeader>
          <h2 class="h6 mb-0">Invoices and payments</h2>
        </CCardHeader>
        <CCardBody>
          <CDataTable
            :items="invoices"
            :fields="invoiceFields"
            items-per-page-select
            :items-per-page="25"
            hover
            sorter
            pagination
            responsive
          >
            <template #issueDate="{ item }">
              <td>{{ formatDate(item.issueDate) }}</td>
            </template>
            <template #dueDate="{ item }">
              <td>{{ formatDate(item.dueDate) }}</td>
            </template>
            <template #paymentDate="{ item }">
              <td>{{ formatDate(item.paymentDate) }}</td>
            </template>
            <template #amount="{ item }">
              <td class="text-right">RM {{ formatMyr(item.amount) }}</td>
            </template>
          </CDataTable>
        </CCardBody>
      </CCard>
    </template>

    <!-- Delete confirmation -->
    <CModal :show.sync="warningModal" :centered="true" :close-on-backdrop="!deleting">
      <template #header>
        <h5 class="modal-title">Delete the {{ itemToDelete.packageName }} subscription?</h5>
      </template>
      <p class="mb-0">
        The {{ itemToDelete.packageName }} subscription for
        {{ formatDate(itemToDelete.startDate) }} – {{ formatDate(itemToDelete.endDate) }}
        will be removed from your history. This can't be undone.
      </p>
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">
          Keep it
        </CButton>
        <CButton color="danger" :disabled="deleting" @click="confirmDelete">
          <CSpinner v-if="deleting" size="sm" class="mr-1" />
          Delete subscription
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import { loadStripe } from "@stripe/stripe-js";
import SubscriptionApi from "@/lib/subscriptionApi";
import PackageApi from "@/lib/packageApi";
import PaymentApi from "@/lib/paymentApi";
import { formatDate } from "@/utils/datetime";
import { formatMyr } from "@/utils/money";

// Section 3.3 `subscription` map (SubscriptionStatus)
const SUBSCRIPTION_STATUS = {
  Pending: { color: "warning", label: "Pending" },
  NotPaid: { color: "warning", label: "Not paid" },
  Paid: { color: "success", label: "Paid" },
  Expired: { color: "danger", label: "Expired" },
  Cancelled: { color: "secondary", label: "Cancelled" },
};

export default {
  name: "SubscriptionList",
  data() {
    return {
      // Online upgrade is not live yet (the buttons were disabled); keep it hidden until it is.
      upgradeEnabled: false,
      paymentApi: new PaymentApi(),
      stripe: null, // Stripe instance
      cardElement: null, // Stripe Card Element
      cardError: "", // Holds any Stripe error messages
      packageItems: [],
      packageApi: new PackageApi(),

      showUpgradeModal: false, // To control the visibility of the modal
      selectedPackage: "", // To hold the selected package
      selectedPaymentMethod: "", // To hold the selected payment method
      paymentMethodOptions: [
        { value: "", label: "Choose a payment method", disabled: true },
        { value: "Online", label: "Online banking" },
        { value: "CreditCard", label: "Credit or debit card" },
      ],
      paying: false,

      loading: true,
      error: false,
      deleting: false,
      subscriptionHistory: [],
      invoices: [],
      fields: [
        { key: "startDate", label: "Start date" },
        { key: "endDate", label: "End date" },
        { key: "packageName", label: "Plan" },
        { key: "isActive", label: "Active" },
        { key: "statusDescription", label: "Status" },
        { key: "amount", label: "Amount", _classes: "text-right" },
        {
          key: "show_details",
          label: "",
          _style: "width:1%",
          sorter: false,
          filter: false,
        },
      ],
      invoiceFields: [
        { key: "invoiceNumber", label: "Invoice no." },
        { key: "issueDate", label: "Issue date" },
        { key: "dueDate", label: "Due date" },
        { key: "amount", label: "Amount", _classes: "text-right" },
        { key: "status", label: "Status" },
        { key: "paymentDate", label: "Payment date" },
      ],
      api: new SubscriptionApi(),
      warningModal: false,
      itemToDelete: {},
    };
  },
  mounted() {
    if (this.upgradeEnabled) this.fetchPackageList();
    this.refreshTable();
  },
  watch: {
    selectedPaymentMethod(newMethod) {
      if (newMethod === "CreditCard") {
        this.$nextTick(() => this.initializeStripe());
      } else {
        this.destroyStripe();
      }
    },
  },

  computed: {
    activeSubscription() {
      return this.computedSubscriptionHistory.find(
        (sub) => sub.isActive === true
      );
    },
    computedSubscriptionHistory() {
      return this.subscriptionHistory.map((item) => {
        return {
          ...item,
          packageName: item.package ? item.package.name : "",
        };
      });
    },
  },
  methods: {
    formatDate,
    formatMyr,
    statusColor(status) {
      return (SUBSCRIPTION_STATUS[status] || {}).color || "secondary";
    },
    statusLabel(status) {
      return (SUBSCRIPTION_STATUS[status] || {}).label || status;
    },
    formatHtml(item) {
      return (item || "").replace(/\n/g, "<br />");
    },
    destroyStripe() {
      if (this.cardElement) {
        this.cardElement.unmount();
        this.cardElement = null;
      }
    },

    initializeStripe() {
      loadStripe("pk_test_Wx2rVD350hgX6aUVtgqtKOVo")
        .then((stripeInstance) => {
          this.stripe = stripeInstance;
          const elements = this.stripe.elements();

          // Stripe Elements render in an iframe and only accept inline style values.
          this.cardElement = elements.create("card", {
            style: {
              base: {
                fontFamily: 'Roboto, "Helvetica Neue", Helvetica, sans-serif',
                fontSmoothing: "antialiased",
              },
            },
          });

          this.cardElement.mount(this.$refs.cardElement);

          this.cardElement.on("change", (event) => {
            if (event.error) {
              this.cardError = event.error.message;
            } else {
              this.cardError = "";
            }
          });
        })
        .catch((error) => {
          console.error(error);
          this.$toast.error("Couldn't load the card form. Refresh the page and try again.");
        });
    },

    proceedToPayment() {
      if (!this.selectedPackage || !this.selectedPaymentMethod) {
        this.$toast.warning("Choose a plan and a payment method first");
        return;
      }

      if (this.selectedPaymentMethod === "CreditCard") {
        this.paying = true;
        // Stripe Payment Intent creation
        this.stripe
          .createPaymentMethod({
            type: "card",
            card: this.cardElement,
            billing_details: {
              name: "Customer Name", // Replace with dynamic customer data
            },
          })
          .then(({ error, paymentMethod }) => {
            if (error) {
              // Stripe card errors are written for customers (e.g. "Your card was declined.")
              this.cardError = error.message;
              this.paying = false;
            } else {
              this.createPaymentIntent(paymentMethod.id);
            }
          })
          .catch((error) => {
            console.error(error);
            this.paying = false;
            this.$toast.error("Couldn't process your card. Check the details and try again.");
          });
      }
    },

    createPaymentIntent(paymentMethodId) {
      const paymentIntentCreateRequestDto = {
        paymentMethodId,
        package: this.selectedPackage,
        amount: this.selectedPackage.price * 100,
      };

      this.paymentApi
        .createPaymentIntent(paymentIntentCreateRequestDto)
        .then((response) => {
          var clientSecret = response.result.clientSecret;
          return this.stripe.confirmCardPayment(clientSecret).then(({ error }) => {
            if (error) {
              this.cardError = error.message;
            } else {
              this.$toast.success("Payment received. Your plan is upgraded");
              this.showUpgradeModal = false; // Close modal on success
              this.refreshTable(); // Refresh data to show updated subscription
            }
          });
        })
        .catch((err) => {
          console.error(err);
          this.$toast.error("Couldn't start the payment. You haven't been charged. Try again.");
        })
        .finally(() => {
          this.paying = false;
        });
    },

    isSelectedColor(item) {
      try {
        if (item.id == this.selectedPackage.id) return "primary";
        else return "light";
      } catch (error) {
        return "light";
      }
    },
    onSelectPackage(item) {
      this.selectedPackage = item;
    },
    fetchPackageList() {
      this.packageApi
        .getListByActive()
        .then((response) => {
          this.packageItems = response.result;
        })
        .catch((err) => {
          console.error(err);
          this.$toast.error("Couldn't load the plans. Check your connection and try again.");
        });
    },

    refreshTable() {
      this.loading = true;
      this.error = false;
      this.api
        .getListByCurrentTenant()
        .then((response) => {
          this.subscriptionHistory = response.result || [];
          this.invoices = response.invoices || []; // Assuming invoices are part of the response
        })
        .catch((err) => {
          console.error(err);
          this.error = true;
          if (this.subscriptionHistory.length) {
            this.$toast.error("Couldn't refresh your subscription. Check your connection and try again.");
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    upgradeToPro() {
      this.selectedPaymentMethod = "";
      this.showUpgradeModal = true; // Show the upgrade modal
    },
    onEdit(item) {
      this.$router.push({
        path: `/tenants/Subscription/${item.id}`,
      });
    },
    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },
    confirmDelete() {
      this.deleting = true;
      this.api
        .delete(this.itemToDelete.id)
        .then(() => {
          this.$toast.success("Subscription deleted");
          this.warningModal = false;
          this.itemToDelete = {};
          this.refreshTable();
        })
        .catch(({ data }) => {
          this.$toast.error(
            `Couldn't delete the subscription. ${helper.getErrorMessage(data)}`
          );
        })
        .finally(() => {
          this.deleting = false;
        });
    },
  },
};
</script>
